/** @jsx jsx */

import * as React from "react";
import { css, jsx } from "@emotion/core";
import { DESIGN_TOKEN } from "Config/design_token";
require("Views/assets/sandpiper_logo_white.svg");

/** Props used by the hero component. */
export type Props = {
  /**
   * Determines how the hero should render, applying various styles for
   * small screens or large.
   */
  screenSize: "SMALL" | "LARGE";
  /** The source file for the image used by the hero on small screens. */
  srcSmall: string;
  /** the source file for the image used by the hero on large screens. */
  srcLarge: string;
  /** The alt-text value for the source image. */
  srcAltText: string;
};

type State = {
  /** If the hero image has loaded. */
  imageLoaded: boolean;
};

function Hero(props: Props): JSX.Element {
  const { screenSize, srcSmall = "", srcLarge = "", srcAltText } = props;

  const [imageLoaded, setImageLoaded] = React.useState<State["imageLoaded"]>(
    false
  );

  const handleImageLoaded = () => {
    setImageLoaded(true);
  };

  /**
   * Ensures the correct image is loaded based on screen size and if a
   * corresponding source is provided.  Managing this through state allows for
   * a fade-in animation.  Because this involves event listeners, useEffect is
   * leveraged to unsubscribe from events when the component unmounts,
   * preventing a memory leak.
   * @see https://reactjs.org/docs/hooks-effect.html#example-using-hooks-1
   */
  React.useEffect(() => {
    setImageLoaded(false);

    const img = new Image();
    if (srcSmall !== "" && screenSize === "SMALL") {
      img.src = srcSmall;
    } else if (srcLarge !== "" && screenSize === "LARGE") {
      img.src = srcLarge;
    }
    img.addEventListener("load", handleImageLoaded);

    return function cleanup() {
      img.removeEventListener("load", handleImageLoaded);
    };
  }, [screenSize]);

  return (
    <div
      css={css`
        width: 100%;
        max-width: 100%;
        height: auto;
        margin: 0rem;
        padding: 0rem;
        background-color: ${DESIGN_TOKEN.colors.swatch_3};
      `}
    >
      <div
        css={css`
          display: flex;
          position: relative;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: ${screenSize === "SMALL" ? "100vh" : "50vh"};
          opacity: ${imageLoaded ? 1 : 0};
          transition: opacity 0.3s;
        `}
      >
        {srcSmall !== "" && imageLoaded === true && (
          <img
            src={screenSize === "SMALL" ? srcSmall : srcLarge}
            alt={srcAltText}
            css={css`
              display: flex;
              position: absolute;
              z-index: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
            `}
          />
        )}
        {screenSize === "SMALL" && (
          <div
            css={css`
              display: flex;
              position: relative;
              width: 100%;
              height: 100%;
              align-items: center;
              justify-content: center;
              background-color: rgba(0, 0, 0, 0.3);
            `}
          >
            <img
              src="/wp-content/themes/sandpiper/img/sandpiper_logo_white.svg"
              alt="The Sandpiper Nursery School"
              css={css`
                width: 30rem;
                max-width: 90%;
                height: auto;
              `}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const heroMemo = React.memo<Props>(Hero);
export { heroMemo as Hero };
